import {
  type ApiClient,
  ConfigsStepPostSignQa,
  ConfigsStepPostSignQaAnswers,
  UserLookingForContract,
} from '@prismo-io/schemas';
import { z } from 'zod';

const ParamsSchema = z.object({
  questionId: ConfigsStepPostSignQa.shape.id,
  answerIds: z.array(ConfigsStepPostSignQaAnswers.shape.id).min(1),
  complement: z.string().optional(),
  lookingForContract: UserLookingForContract.optional(),
});

export type ParamsSchemaT = z.infer<typeof ParamsSchema>;

export const answerToQuestion = async (
  client: ApiClient,
  params: ParamsSchemaT
) => {
  const { questionId, answerIds, complement, lookingForContract } =
    ParamsSchema.parse(params);

  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session) {
    throw new Error('You must be connected.');
  }

  const { data: currentAnswers } = await client
    .from('configs_step_post_sign_qa')
    .select(
      `id,
            answers:configs_step_post_sign_qa_answers!inner(
                id,
                responses:configs_step_post_sign_qa_responses!inner(
                    user_id
                )
            )
            `
    )
    .eq('id', questionId)
    .eq(
      'configs_step_post_sign_qa_answers.configs_step_post_sign_qa_responses.user_id',
      session.user.id
    )
    .maybeSingle();

  if (currentAnswers && currentAnswers.answers.length > 0) {
    await client
      .from('configs_step_post_sign_qa_responses')
      .delete()
      .in(
        'answer_id',
        currentAnswers?.answers.map((a) => a.id)
      )
      .eq('user_id', session.user.id);
  }

  await client.from('configs_step_post_sign_qa_responses').insert(
    answerIds.map((answer) => {
      return {
        answer_id: answer,
      };
    })
  );

  if (complement) {
    await client
      .from('users')
      .update({
        source: complement,
      })
      .eq('id', session.user.id);
  }

  if (lookingForContract) {
    await client
      .from('users')
      .update({
        looking_for_contract: lookingForContract,
      })
      .eq('id', session.user.id);
  }

  return true;
};
